// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { View } from "@react-pdf/renderer";
import React from "react";

import { formatLongDate, joinNonEmptyParts } from "../../../../utils/utils";
import { getAssessmentTypeShortName } from "../../../OffenderAssessment/assessmentTypeUtils";
import {
  getDomainsForAssessmentType,
  shouldShowOrasContent,
} from "../../../OffenderAssessment/utils";
import { SARSection } from "../../../SARDetails/constants";
import { shouldShowInSARReport } from "../../../SARDetails/utils";
import { ageAtAssessment as getAgeAtAssessment } from "../derive";
import { HeadingMeta } from "../primitives/HeadingMeta";
import { Paragraph } from "../primitives/Paragraph";
import { UnderlinedHeading } from "../primitives/UnderlinedHeading";
import { useActiveFeatureVariants, useSAR } from "../SARContext";
import type { PdfStyle } from "../SARPdfTemplate.types";
import { OrasDomainSection } from "./OrasDomainSection";

/**
 * "Offender Risk Assessment" section — heading + one banner per ORAS domain
 * for this assessment type. Responsivity Issues & Barriers is just another
 * domain config entry (no risk pip), so it falls out of the same map.
 *
 * When the defendant declined to participate, this mirrors the DOM report
 * (ReportOffenderAssessment): the title drops the ORAS type, the "Administered
 * By" note is omitted, the defendant statement (the officer's contact-attempt
 * notes) shows under the heading, and only the Criminal History domain renders
 * — `getDomainsForAssessmentType(null)` returns just that config, and
 * `OrasDomainSection` already suppresses the risk pip when declined.
 */
export const OrasAssessment: React.FC<{ style?: PdfStyle }> = ({
  style = {},
}) => {
  const { sar } = useSAR();
  const activeFeatureVariants = useActiveFeatureVariants();
  if (!shouldShowInSARReport(SARSection.OFFENDER_ASSESSMENT, sar)) return null;
  const declined = sar.defendantDeclinedToParticipate;
  const domains = getDomainsForAssessmentType(
    declined ? null : sar.assessmentType,
  );
  const administeredBy = sar.assessmentAdministeredBy;
  const administeredOn = sar.assessmentDate;
  const ageAtAssessment = getAgeAtAssessment(sar);
  const meta =
    !declined && administeredBy
      ? joinNonEmptyParts(
          [
            joinNonEmptyParts(
              [
                `Administered By: ${administeredBy}`,
                administeredOn
                  ? formatLongDate(new Date(administeredOn))
                  : null,
              ],
              ", ",
            ),
            ageAtAssessment != null
              ? `Age at Assessment: ${ageAtAssessment}`
              : null,
          ],
          " | ",
        )
      : undefined;
  const statement =
    declined && sar.defendantStatement ? sar.defendantStatement : null;
  const noOrasReason =
    !shouldShowOrasContent(sar.ORASDomainsAvailable, activeFeatureVariants) &&
    sar.noORASDomainReason
      ? sar.noORASDomainReason
      : null;
  const [firstDomain, ...remainingDomains] = domains;

  return (
    <View style={style}>
      {/* Keeps the heading on the same page as the first domain, which is
          itself unbreakable. Only when no paragraph sits between them: a
          free-text paragraph can be longer than a page, and the heading then
          flows with the paragraph instead. */}
      <View wrap={!!statement || !!noOrasReason}>
        <UnderlinedHeading>
          {declined
            ? "Offender Risk Assessment"
            : `Offender Risk Assessment (${getAssessmentTypeShortName(sar.assessmentType)})`}
        </UnderlinedHeading>
        {meta ? <HeadingMeta>{meta}</HeadingMeta> : null}
        {statement ? <Paragraph>{statement}</Paragraph> : null}
        {noOrasReason ? <Paragraph>{noOrasReason}</Paragraph> : null}
        {firstDomain ? <OrasDomainSection domain={firstDomain} /> : null}
      </View>
      {remainingDomains.map((domain) => (
        <OrasDomainSection key={domain.key} domain={domain} />
      ))}
    </View>
  );
};
